import React, { useContext, useEffect, useMemo, useState } from 'react'
import { graphql, useScrollRestoration, navigate } from 'gatsby'
import IsPageUseful from '../components/IsPageUseful'
import Newsletter from '../components/Newsletter'
import LanguageContext from '../contexts/languageContext'
import VersionContext from '../contexts/versionContext'
import PageContext from '../contexts/pageContext'
import { AuthContext } from '../contexts/authContext'
import Header from '../components/Header'
import styles from '../styles.module.css'
import useMounted from '../hooks/useMounted'
import { getLanguageFromUrl } from '../utils/navigation'
import Seo from '../components/SEO'
import { renderAst } from '../helpers/html'
import { checkIsPrivateSlug, checkHasAuthorizationToResource } from '../helpers/authorization'
import UseCasesRelated from '../components/UseCases/UseCasesRelated'
import LoadingOverlay from '../components/LoadingOverlay'

const PostTemplate = ({ data, pageContext: context, location }) => {
  const { fields, frontmatter, htmlAst } = data.markdownRemark
  const post = data.markdownRemark
  const { category } = context
  const [isLoading, setIsLoading] = useState(true)
  const { setCurrentPage } = useContext(PageContext)
  const { setLanguage } = useContext(LanguageContext)
  const { setVersion } = useContext(VersionContext)
  const { resourceAllowed, isLoading: isAuthLoading } = useContext(AuthContext)
  const hasMounted = useMounted()
  const pageScrollRestoration = useScrollRestoration()
  const distinctLangs = data && data.allMarkdownRemark && data.allMarkdownRemark.distinct
  const isPrivateSlug = useMemo(() => checkIsPrivateSlug(fields.slug), [fields.slug])

  useEffect(() => {
    setLanguage(getLanguageFromUrl(location.pathname))
    setVersion(
      data.markdownRemark.fields.verKey != null && data.markdownRemark.fields.verKey.length > 0
        ? data.markdownRemark.fields.verKey[0].slice(1)
        : ''
    )
    setCurrentPage(fields.slug)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!isPrivateSlug) {
      setIsLoading(false)
    }
  }, [isPrivateSlug])

  useEffect(() => {
    if (isPrivateSlug && !isAuthLoading) {
      const hasAuthorizationToResource = checkHasAuthorizationToResource(resourceAllowed, fields.slug)

      hasAuthorizationToResource ? setIsLoading(false) : navigate('/')
    }
  }, [isPrivateSlug, resourceAllowed, fields.slug, isAuthLoading, location])

  const showCategory = !!category || (frontmatter && !!frontmatter.category)

  const getPageTitle = () => {
    if (post && post.frontmatter) {
      return post.frontmatter.title
    }
    return 'Hotmart Developers'
  }

  const noIndex = /\/404\/$/.test(location.pathname)

  //TODO verificar se categoria existe - FAQ, 404
  return !isLoading && hasMounted ? (
    <>
      <Seo
        category={post && post.frontmatter && post.frontmatter.category}
        description={post && post.frontmatter && post.frontmatter.description}
        distinctLangs={distinctLangs}
        locale={post && post.fields && post.fields.langKey}
        noIndex={noIndex}
        tags={post && post.frontmatter && post.frontmatter.tags}
        title={getPageTitle()}
        url={post && post.fields && post.fields.slug}
      />
      <div className={styles.wrapper} {...pageScrollRestoration}>
        {showCategory && (
          <Header
            title={frontmatter.category || frontmatter.title}
            description={frontmatter.categoryDescription || frontmatter.description}
            buttonLink={frontmatter.buttonLink}
            buttonText={frontmatter.buttonText}
            buttonIcon={frontmatter.buttonIcon}
          />
        )}
        <div className={styles.contentContainer}>
          <div className={styles.content}>{renderAst(htmlAst)}</div>
        </div>

        <IsPageUseful url={location.href} />
        <UseCasesRelated category={frontmatter.api || category} />
        <Newsletter />
      </div>
    </>
  ) : (
    <LoadingOverlay />
  )
}

export const query = graphql`
  query($slug: String!, $pathNoLang: String!) {
    markdownRemark(fields: { slug: { eq: $slug } }) {
      htmlAst
      frontmatter {
        description
        title
        tags
        category
        categoryDescription
        api
        buttonLink
        buttonText
        buttonIcon
      }
      fields {
        langKey
        verKey
        slug
      }
    }
    allMarkdownRemark(filter: { fields: { pathNoLang: { eq: $pathNoLang } } }) {
      distinct(field: fields___langKey)
    }
  }
`

export default PostTemplate
